import { clearSession, setSession } from '../auth/sessionStore';
import { apiBaseUrl } from './baseUrl';

/**
 * The silent refresh (`FEAT-003 §5.3`).
 *
 * It sends no body. The credential is the `tms_refresh` cookie the browser attaches on its own, which is
 * why `credentials: 'include'` is the only thing that makes this work and why no code here reads a token.
 *
 * **A 401 storm cannot loop**, and three separate things hold that:
 *
 * 1. **One refresh at a time.** Every caller that arrives while one is in flight waits on the same
 *    promise. Ten requests failing together produce one refresh, not ten.
 * 2. **A refused refresh is remembered.** The server clears the cookie on a `401` (`FEAT-003 §5.3`), so
 *    presenting it again is guaranteed to fail; `refused` makes every later attempt answer `false`
 *    immediately, without a request. Signing in clears it.
 * 3. **Refresh never refreshes.** This uses `fetch` directly rather than the typed client, so the client's
 *    retry-on-401 cannot reach it. That is the recursion the other two would not catch.
 */
let inFlight: Promise<boolean> | null = null;
let refused = false;

/** The one path this module knows. Kept here because `src/api/` is where API paths are written. */
const REFRESH_PATH = '/v1/auth/refresh';

/**
 * Asks for a new access token. Resolves true when there is one.
 *
 * Safe to call from anywhere, as often as anything likes: concurrent calls share one request, and calls
 * after a refusal cost nothing.
 */
export async function refreshSession(): Promise<boolean> {
  if (refused) {
    return false;
  }

  inFlight ??= attempt().finally(() => {
    inFlight = null;
  });

  return inFlight;
}

/**
 * Forgets that a refresh was refused. Sign-in calls it, because a new sign-in sets a new cookie and the
 * old refusal says nothing about it.
 */
export function allowRefreshAgain(): void {
  refused = false;
}

/** For tests, which need a module with no memory of the last one. */
export function resetRefreshForTests(): void {
  refused = false;
  inFlight = null;
}

async function attempt(): Promise<boolean> {
  try {
    const response = await globalThis.fetch(`${apiBaseUrl()}${REFRESH_PATH}`, {
      method: 'POST',
      // The cookie is the credential. Without this the request carries nothing and always fails.
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
    });

    if (!response.ok) {
      // The server has cleared the cookie, so there is nothing left to try (`FEAT-003 §5.3`).
      refused = true;
      clearSession();
      return false;
    }

    const body = (await response.json()) as { accessToken?: string; emailVerified?: boolean };
    if (!body.accessToken) {
      refused = true;
      clearSession();
      return false;
    }

    setSession({ accessToken: body.accessToken, emailVerified: body.emailVerified ?? false });
    return true;
  } catch {
    // A dropped connection is not a refusal. The cookie may still be good, so this does *not* latch —
    // it only reports that there is no session right now.
    clearSession();
    return false;
  }
}
